import { Link } from "react-router-dom";
import { ArrowRight, Sparkles, Phone } from "lucide-react";
import { motion, useScroll, useTransform } from "framer-motion";
import { useRef } from "react";

const CTABanner = () => {
  const ref = useRef<HTMLDivElement>(null);
  const { scrollYProgress } = useScroll({
    target: ref,
    offset: ["start end", "end start"],
  });
  const bgY = useTransform(scrollYProgress, [0, 1], ["-20%", "20%"]);

  return (
    <section ref={ref} className="relative overflow-hidden py-28 md:py-36">
      {/* Animated background */}
      <motion.div className="absolute inset-0 bg-gradient-to-br from-background via-primary/5 to-background" style={{ y: bgY }} />
      <div className="absolute inset-0 bg-diagonal opacity-20" />
      
      {/* Floating orbs with pulse */}
      <motion.div
        className="absolute top-1/4 left-1/4 w-80 h-80 rounded-full opacity-12 orb-pulse"
        style={{ background: "radial-gradient(circle, hsl(43 72% 52% / 0.4) 0%, transparent 70%)" }}
        animate={{ x: [0, 30, 0], y: [0, -20, 0] }}
        transition={{ duration: 8, repeat: Infinity, ease: "easeInOut" }}
      />
      <motion.div
        className="absolute bottom-1/4 right-1/4 w-64 h-64 rounded-full opacity-8"
        style={{ background: "radial-gradient(circle, hsl(43 72% 52% / 0.3) 0%, transparent 70%)" }}
        animate={{ x: [0, -20, 0], y: [0, 30, 0] }}
        transition={{ duration: 10, repeat: Infinity, ease: "easeInOut" }}
      />

      {/* Central glow */}
      <div
        className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[700px] h-[700px] rounded-full opacity-20"
        style={{
          background: "radial-gradient(circle, hsl(43 72% 52% / 0.25) 0%, transparent 70%)",
        }}
      />

      <div className="relative container-luxury px-4 md:px-8 text-center">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.7 }}
        >
          <motion.div 
            className="inline-flex items-center gap-2 glass-ultra rounded-full px-5 py-2.5 mb-8"
            animate={{ scale: [1, 1.02, 1] }}
            transition={{ duration: 3, repeat: Infinity }}
          >
            <Sparkles size={14} className="text-primary" />
            <span className="text-[11px] font-semibold tracking-[0.2em] uppercase text-primary">Limited Availability</span>
          </motion.div>

          <h2 className="font-display text-3xl md:text-5xl lg:text-6xl font-bold mb-6 text-glow leading-tight">
            Ready for a{" "}
            <span className="gold-gradient-text-animated">Showroom Finish</span>?
          </h2>

          <p className="text-muted-foreground text-lg md:text-xl max-w-2xl mx-auto mb-12 leading-relaxed">
            Book your premium mobile detail today. We come to you — no drop-offs, no waiting. Just stunning results at your doorstep.
          </p>

          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <motion.div whileHover={{ scale: 1.04, y: -2 }} whileTap={{ scale: 0.98 }}>
              <Link
                to="/book"
                className="gold-gradient-bg text-primary-foreground px-10 py-4 text-sm font-semibold tracking-wider rounded-xl transition-all duration-300 hover:shadow-2xl hover:shadow-primary/25 inline-flex items-center justify-center gap-2 btn-luxury btn-magnetic animate-pulse-glow"
              >
                Book Now <ArrowRight size={16} />
              </Link>
            </motion.div>
            <motion.div whileHover={{ scale: 1.04, y: -2 }} whileTap={{ scale: 0.98 }}>
              <Link
                to="/pricing"
                className="border border-primary/20 text-foreground px-10 py-4 text-sm font-semibold tracking-wider rounded-xl transition-all duration-300 hover:border-primary/50 hover:bg-primary/5 hover:shadow-lg hover:shadow-primary/10 inline-flex items-center justify-center gap-2"
              >
                View Pricing
              </Link>
            </motion.div>
          </div>

          {/* Phone number */}
          <motion.div
            className="mt-10"
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            viewport={{ once: true }}
            transition={{ delay: 0.5 }}
          >
            <a href="tel:+17035551234" className="inline-flex items-center gap-2 text-muted-foreground hover:text-primary transition-colors text-sm group">
              <span className="w-8 h-8 rounded-full glass-premium flex items-center justify-center group-hover:border-primary/30 transition-colors">
                <Phone size={13} />
              </span>
              Or call us directly
            </a>
          </motion.div>
        </motion.div>
      </div>
    </section>
  );
};

export default CTABanner;
